import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import apiClient from '../api/client';
import PreviewRaceDashboardPage from '../components/predictionService/PreviewRaceDashboardPage';
import { buildRaceDetailModel } from '../components/raceDetail/utils';

const NAR_TABLE_COLUMNS = [
  { key: 'horseNumber', label: '馬番', align: 'center', type: 'number' },
  { key: 'horseName', label: '馬名', type: 'name' },
  { key: 'odds', label: '単勝オッズ', align: 'right', type: 'odds' },
  { key: 'winRate', label: '勝率', align: 'right', type: 'percent' },
  { key: 'expectedValue', label: 'AI指数', align: 'right', type: 'multiplier' },
  { key: 'aiRank', label: 'AI評価', align: 'center', type: 'rank' },
];

const EMPTY_PAGE_DATA = {
  availableDates: [],
  racesByDate: {},
  previewNotice: '地方競馬の最新予測データを準備しています。',
  raceNotice: '',
  helpBody:
    '地方競馬の最新予測JSONをもとに、採用馬と全馬予測をダッシュボード表示します。selected_predictions が空でも異常ではなく、その日は推奨なしで予測自体は生成済みです。',
};

const formatUpdatedAt = (updatedAt) => {
  if (!updatedAt) return '';
  const date = new Date(Number(updatedAt) * 1000);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toNumber = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const normalized = typeof value === 'string' ? value.replace(/,/g, '').trim() : value;
  const num = Number(normalized);
  return Number.isFinite(num) ? num : null;
};

const hasNarRecommendedBets = (payload) => {
  const selectedPredictionCount =
    toNumber(payload?.counts?.selected_predictions) ??
    (Array.isArray(payload?.selected_predictions) ? payload.selected_predictions.length : 0);
  const pendingBetCount = toNumber(payload?.pending_bets?.bets) ?? 0;
  return selectedPredictionCount > 0 || pendingBetCount > 0;
};

const hasRaceSelectedBet = (race) => {
  if (race?.has_selected_bet === true) return true;
  const selectedCount = toNumber(race?.selected_count);
  if (selectedCount !== null) return selectedCount > 0;
  return (race?.predictions || []).some((prediction) => prediction?.selected);
};

const hasRacePositiveEvHorse = (race) =>
  (race?.predictions || []).some((prediction) => (toNumber(prediction?.pred_ev) ?? 0) >= 1);

const normalizeNarRaceConfidence = (value) => {
  const normalized = String(value || '').trim().toUpperCase();
  return ['S', 'A', 'B'].includes(normalized) ? normalized : null;
};

const deriveNarRaceConfidence = (race) => {
  const confidenceLabel = normalizeNarRaceConfidence(race?.confidence_label);
  if (confidenceLabel) return confidenceLabel;
  if (hasRaceSelectedBet(race)) return 'S';
  if (hasRacePositiveEvHorse(race)) return 'A';
  return 'B';
};

const sortNarPredictions = (predictions) =>
  [...(predictions || [])].sort((a, b) => {
    const probDiff = (toNumber(b?.pred_prob) ?? 0) - (toNumber(a?.pred_prob) ?? 0);
    if (probDiff !== 0) return probDiff;
    const evDiff = (toNumber(b?.pred_ev) ?? 0) - (toNumber(a?.pred_ev) ?? 0);
    if (evDiff !== 0) return evDiff;
    return (toNumber(a?.umaban) ?? 99) - (toNumber(b?.umaban) ?? 99);
  });

const deriveRoughness = (horses) => {
  const top3 = (horses || []).slice(0, 3);
  if (top3.length < 3 || top3.some((horse) => horse.odds === null || horse.odds === undefined)) {
    return null;
  }
  const oddsSum = top3.reduce((sum, horse) => sum + (horse.odds ?? 0), 0);
  if (oddsSum <= 12) return 'C';
  if (oddsSum <= 20) return 'B';
  if (oddsSum <= 30) return 'A';
  return 'S';
};

const buildRaceKey = (race) =>
  race.race_id ||
  [race.race_date, race.place_name, race.race_no].filter(Boolean).join('_') ||
  race.race_name;

const buildRaceName = (race) =>
  race.race_name ||
  `${race.place_name || ''}${race.race_label || (race.race_no ? `${race.race_no}R` : '')}` ||
  buildRaceKey(race);

const buildTanpukuRows = (race) => {
  const sorted = sortNarPredictions(race.predictions);

  const rankMap = new Map(sorted.map((prediction, index) => [String(prediction.umaban ?? ''), index + 1]));

  return sorted.map((prediction) => ({
    umaban: prediction.umaban,
    horse_name:
      String(prediction.entry_horse_name || '').trim() || `馬番 ${prediction.umaban ?? '-'}`,
    tansho_odds: toNumber(prediction.win_odds),
    p: toNumber(prediction.pred_prob),
    ev: toNumber(prediction.pred_ev),
    pick: prediction.selected ? 1 : 0,
    p_rank_in_race: rankMap.get(String(prediction.umaban ?? '')) ?? null,
  }));
};

const buildNarDetailModel = (race) => {
  const tanpukuRows = buildTanpukuRows(race);
  if (!tanpukuRows.length) return null;

  const raceKey = buildRaceKey(race);
  const raceName = buildRaceName(race);
  const selectedCount =
    toNumber(race.selected_count) ?? tanpukuRows.filter((row) => Number(row.pick) === 1).length;
  const confidence = deriveNarRaceConfidence(race);
  const data = {
    tanpuku_df: tanpukuRows,
    umaren_df: [],
    sanrenpuku_df: [],
    sanrentan_df: [],
    race_name: raceName,
  };

  const initialMeta = {
    race_name: raceName,
    race_key: raceKey,
    surface_label: race.surface_label || 'ダート',
  };
  const initialModel = buildRaceDetailModel({
    data,
    selectedRaceMeta: initialMeta,
    selectedDate: race.race_date,
  });
  if (!initialModel) return null;

  const roughness = deriveRoughness(initialModel.horses);
  const detailModel =
    buildRaceDetailModel({
      data,
      selectedRaceMeta: {
        ...initialMeta,
        roughness_raw: roughness,
        confidence_raw: confidence,
      },
      selectedDate: race.race_date,
    }) || initialModel;
  const representativePrediction = sortNarPredictions(race.predictions)[0] || null;
  const representativeHorse = representativePrediction
    ? detailModel.horses.find(
        (horse) => String(horse.horseNumber ?? '') === String(representativePrediction.umaban ?? '')
      )
    : null;

  return {
    ...detailModel,
    title: `${raceName}のAI分析`,
    subtitle: '地方競馬AIデータ解析による予想分析',
    raceConfidence: {
      ...(detailModel.raceConfidence || {}),
      label: confidence,
      tone: confidence.toLowerCase(),
    },
    recommendedHorses: representativeHorse
      ? [
          {
            ...representativeHorse,
            aiRank: confidence,
          },
        ]
      : detailModel.recommendedHorses,
    recommendEyebrow: 'RACE PICK',
    recommendTitle: '代表馬',
    tableColumns: NAR_TABLE_COLUMNS,
    stats: [
      { label: '解析頭数', value: `${detailModel.horses.length}頭` },
      { label: '発走', value: race.start_time_live || '-' },
      { label: 'トップ勝率', value: detailModel.stats?.[1]?.value || '-' },
      { label: '上位3頭オッズ合計', value: detailModel.stats?.[2]?.value || '-' },
      { label: '採用馬数', value: `${selectedCount}頭` },
    ],
  };
};

const buildNarPageData = (payload) => {
  const racesByDate = {};

  (payload?.races || []).forEach((race) => {
    const raceDate = race.race_date || payload?.available_dates?.[0] || '';
    const pushRace = (entry) => {
      if (!racesByDate[raceDate]) {
        racesByDate[raceDate] = [];
      }
      racesByDate[raceDate].push(entry);
    };

    // 未契約のレースはサーバが predictions を空にして返す（#34）。ここで捨てると
    // 一覧から消えてしまうので、ロックの札を付けたまま残す（#36）
    if (race.locked) {
      pushRace({
        race_key: buildRaceKey(race),
        race_name: buildRaceName(race),
        place_name: race.place_name || '',
        race_no: race.race_no,
        start_time_live: race.start_time_live || '',
        locked: true,
        is_free: false,
        detailModel: null,
      });
      return;
    }

    const detailModel = buildNarDetailModel(race);
    if (!detailModel) return;

    pushRace({
      locked: false,
      is_free: Boolean(race.is_free),
      race_key: buildRaceKey(race),
      race_name: buildRaceName(race),
      place_name: race.place_name || '',
      race_no: race.race_no,
      start_time_live: race.start_time_live || '',
      has_selected_bet: hasRaceSelectedBet(race),
      selected_count:
        toNumber(race.selected_count) ??
        (race.predictions || []).filter((prediction) => prediction?.selected).length,
      confidence_label: normalizeNarRaceConfidence(race.confidence_label),
      surface_label: race.surface_label || 'ダート',
      roughness_raw: detailModel.roughness,
      confidence_raw: deriveNarRaceConfidence(race),
      detailModel,
    });
  });

  Object.values(racesByDate).forEach((raceList) => {
    raceList.sort((a, b) => {
      const placeDiff = String(a.place_name || '').localeCompare(String(b.place_name || ''), 'ja');
      if (placeDiff !== 0) return placeDiff;
      const aRaceNo = toNumber(a.race_no) ?? toNumber(String(a.race_name).match(/(\d+)R$/)?.[1]) ?? 99;
      const bRaceNo = toNumber(b.race_no) ?? toNumber(String(b.race_name).match(/(\d+)R$/)?.[1]) ?? 99;
      if (aRaceNo !== bRaceNo) return aRaceNo - bRaceNo;
      return String(a.race_name).localeCompare(String(b.race_name), 'ja');
    });
  });

  const derivedDates = Object.keys(racesByDate).sort().reverse();
  const availableDates = (payload?.available_dates || []).filter((date) => racesByDate[date]);
  const finalDates = availableDates.length ? availableDates : derivedDates;
  const updatedLabel = formatUpdatedAt(payload?.updated_at);
  const hasRecommendedBets = hasNarRecommendedBets(payload);
  return {
    availableDates: finalDates,
    racesByDate,
    previewNotice: payload?.file_exists
      ? `地方競馬の最新予測JSONを表示しています。15分更新に追随します。${updatedLabel ? ` 最終更新 ${updatedLabel}` : ''}${hasRecommendedBets ? '' : ' 本日は推奨なしですが、全レース予測は生成済みです。'} 自信度は S=購入推奨あり / A=EV1以上あり / B=それ以外 です。`
      : '地方競馬の最新予測JSONがまだ配置されていません。',
    raceNotice:
      !payload?.file_exists
        ? 'サーバー上の最新JSONが未配置のため、地方競馬ページはまだ表示できません。'
        : finalDates.length === 0
          ? '現在表示できる地方競馬レースがありません。'
          : '',
    helpBody:
      '地方競馬の fixed JSON を読み込み、races[].predictions を全馬表示しています。selected_predictions が空でも正常で、counts.selected_predictions / pending_bets.bets が 0 の日は「推奨なし・予測生成済み」として扱います。自信度は races[].confidence_label を優先表示し、未連携時のみ races[].has_selected_bet=true を S、has_selected_bet=false かつ pred_ev>=1 の馬がいるレースを A、それ以外を B として補完算出しています。代表馬は predictions を pred_prob DESC / pred_ev DESC / umaban ASC で並べた先頭馬です。',
  };
};

function NarModelRaceList() {
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState(EMPTY_PAGE_DATA);

  useEffect(() => {
    let cancelled = false;

    const fetchLivePredictions = async () => {
      setLoading(true);
      try {
        // apiClient がアクセストークンを載せる。未ログインでも無料枠は 200 で返る（#36）
        const response = await apiClient.get('/api/nar_model/live_predictions');
        if (!cancelled) {
          setPageData(buildNarPageData(response.data));
        }
      } catch (error) {
        console.error('地方競馬JSON取得失敗:', error);
        if (!cancelled) {
          setPageData({
            ...EMPTY_PAGE_DATA,
            raceNotice: '地方競馬の最新予測JSON取得に失敗しました。',
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchLivePredictions();

    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          bgcolor: '#050c16',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PreviewRaceDashboardPage
      activeServiceKey="nar"
      selectorEyebrow="Race Selector"
      selectorTitle="地方競馬レース詳細を選択"
      helpTitle="地方競馬版の表示について"
      availableDates={pageData.availableDates}
      racesByDate={pageData.racesByDate}
      previewNotice={pageData.previewNotice}
      raceNotice={pageData.raceNotice}
      helpBody={pageData.helpBody}
    />
  );
}

export default NarModelRaceList;
